import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/esm/Form";
import { BIBLE_BOOKS, BIBLE_BOOKS_WITH_CHAPTERS } from "../utils/bibleBooks";

interface BiblePassageInputProps {
    bookInput: string;
    selectedBook: string;
    chapter: string;
    verses: string;
    chapterError: string;
    onBookChange: (bookInput: string, selectedBook: string) => void;
    onChapterChange: (chapter: string) => void;
    onVersesChange: (verses: string) => void;
    onChapterErrorChange: (error: string) => void;
    /** Unique suffix for datalist/input IDs when multiple instances exist on the same page */
    idSuffix?: string;
}

/**
 * Validated Bible passage input: book autocomplete + chapter number (with range check) + optional verses.
 */
export default function BiblePassageInput({
    bookInput,
    selectedBook,
    chapter,
    verses,
    chapterError,
    onBookChange,
    onChapterChange,
    onVersesChange,
    onChapterErrorChange,
    idSuffix = "",
}: BiblePassageInputProps) {
    const { t } = useTranslation();

    const datalistId = `bibleBookList${idSuffix}`;
    const chapterId = `bibleChapter${idSuffix}`;
    const bookId = `bibleBook${idSuffix}`;
    const versesId = `bibleVerses${idSuffix}`;

    // Validate chapter whenever selectedBook or chapter changes
    useEffect(() => {
        if (selectedBook && chapter) {
            const chapterNum = parseInt(chapter, 10);
            const maxChapters = BIBLE_BOOKS_WITH_CHAPTERS[selectedBook];

            if (Number.isNaN(chapterNum) || chapterNum < 1 || chapterNum > maxChapters) {
                onChapterErrorChange(
                    t("simpleSundayService.chapterError", { book: selectedBook, max: maxChapters }) as string,
                );
            } else {
                onChapterErrorChange("");
            }
        } else {
            onChapterErrorChange("");
        }
    }, [selectedBook, chapter, onChapterErrorChange, t]);

    const handleBookChange = (value: string) => {
        const matched = BIBLE_BOOKS.includes(value) ? value : "";
        onBookChange(value, matched);
    };

    return (
        <>
            <Form.Group className="mb-3" controlId={bookId}>
                <Form.Label>{t("simpleSundayService.bibleBook")}</Form.Label>
                <Form.Control
                    type="text"
                    list={datalistId}
                    id={bookId}
                    value={bookInput}
                    onChange={(e) => handleBookChange(e.target.value)}
                    placeholder={t("simpleSundayService.selectBook") as string}
                    data-testid={`bible-book-input${idSuffix}`}
                />
                {bookInput && !selectedBook && (
                    <Form.Text className="text-warning">
                        {t("simpleSundayService.selectFromList")}
                    </Form.Text>
                )}
            </Form.Group>

            <datalist id={datalistId}>
                {BIBLE_BOOKS.map((book) => (
                    <option key={book} value={book} />
                ))}
            </datalist>

            <Form.Group className="mb-3" controlId={chapterId}>
                <Form.Label>{t("simpleSundayService.chapter")}</Form.Label>
                <Form.Control
                    type="number"
                    value={chapter}
                    onChange={(e) => onChapterChange(e.target.value)}
                    min={1}
                    max={selectedBook ? BIBLE_BOOKS_WITH_CHAPTERS[selectedBook] : 150}
                    disabled={!selectedBook}
                    data-testid={`bible-chapter-input${idSuffix}`}
                />
                {chapterError && (
                    <Form.Text className="text-danger">{chapterError}</Form.Text>
                )}
                {selectedBook && !chapterError && chapter && (
                    <Form.Text className="text-muted">
                        {t("simpleSundayService.chapterHelp", {
                            book: selectedBook,
                            max: BIBLE_BOOKS_WITH_CHAPTERS[selectedBook],
                        })}
                    </Form.Text>
                )}
            </Form.Group>

            <Form.Group className="mb-3" controlId={versesId}>
                <Form.Label>{t("simpleSundayService.verses")}</Form.Label>
                <Form.Control
                    type="text"
                    id={versesId}
                    value={verses}
                    onChange={(e) => onVersesChange(e.target.value)}
                    placeholder={t("simpleSundayService.versesPlaceholder") as string}
                    disabled={!selectedBook || !chapter || !!chapterError}
                    data-testid={`bible-verses-input${idSuffix}`}
                />
                <Form.Text className="text-muted">{t("simpleSundayService.versesHelp")}</Form.Text>
            </Form.Group>
        </>
    );
}
